import { useQuery } from "react-query";
import {
  BaseLogItem,
  ListApiResponseV3,
  LogItem,
  MessageLogItem,
} from "../../types";
import { useState } from "react";
import { fetchLogs, LogItemTypeMap } from "../../shared/queries";
import MessageLogList from "./MessageLogList";
import Button from "../Button";
import { useTranslation } from "react-i18next";
import Pagination from "../Pagination";
import CircleSpinner from "../CircleSpinner";
import { toast } from "react-toastify";
import Icon from "../Icon";

type TabKey = "messages" | "general";

interface BaseLogListProps {
  authorKey: string;
  activeTabs?: TabKey[];
  setQueryParams?: (params: {
    thread_id: string | null;
    query: string | null;
    source: string | null;
  }) => void;
  setCurrentAuthor?: (currentAuthorKey: string) => void;
}

export default function BaseLogList({
  authorKey,
  activeTabs,
  setQueryParams,
  setCurrentAuthor,
}: BaseLogListProps) {
  const [currentTab, setCurrentTab] = useState<TabKey>("messages");
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const { t } = useTranslation();

  const { data, isLoading, refetch } = useQuery<
    ListApiResponseV3<LogItem>,
    Error
  >(
    ["logs", authorKey, page, pageSize],
    () =>
      fetchLogs({
        authorKey,
        type: currentTab as keyof LogItemTypeMap,
        page,
        limit: pageSize,
      }),
    {
      staleTime: 5 * 60 * 1000,
      onError: (error: Error) => {
        toast.error(t(error.message) || t("general.requestError"));
      },
    }
  );

  return (
    <div className="flex h-full">
      {/* Main content area */}
      <div className="flex-1 p-4 overflow-auto flex flex-col justify-between">
        {!isLoading && data?.results && data.results.length > 0 && (
          <>
            <div className="flex-1 overflow-auto">
              {currentTab === "messages" && (
                <MessageLogList
                  logs={data?.results ?? []}
                  setQueryParams={setQueryParams}
                  setCurrentAuthor={setCurrentAuthor}
                />
              )}
              {currentTab === "general" && <div>General content here</div>}
            </div>

            <div className="mt-4">
              <Pagination
                page={page}
                pageSize={pageSize}
                totalItems={data?.tot_locals || 0}
                onPageUpdate={(value: any) => {
                  if (value === "+") {
                    setPage((prev) => prev + 1);
                  } else if (value === "-") {
                    setPage((prev) => (prev > 1 ? prev - 1 : 1));
                  } else {
                    setPage(+value);
                  }
                }}
              />
            </div>
          </>
        ) }
        {isLoading && (
          <div className="flex justify-center items-center h-full">
            <CircleSpinner color={"primary"} />
          </div>
        )}
        {!isLoading && data?.results?.length === 0 && (
          <div className="flex flex-col justify-center items-center h-full">
            <Icon name="no-results" size="100px" />
            <span className="text-gray-500 text-xs font-semibold mt-2">
              {t("table.noResOrNoPermission")}
            </span>
          </div>
        )}

      </div>

      {/* Tab bar */}
      <div className="flex flex-col space-y-2 w-[60px] items-center bg-gray-100 pl-1 h-full">
        {activeTabs?.map((tab, index) => (
          <Button
            key={tab}
            onClick={() => setCurrentTab(tab)}
            className={`w-full py-2 text-xs rounded-r ${
              index === 0 ? "mt-4" : ""
            }`}
            icon="messageLog"
            dataTip={t(`logging.labels.${tab}`)}
            color={currentTab === tab ? "primary" : "info"}
          />
        ))}
      </div>
    </div>
  );
}
